// A filter run that deliberately left the map view out owes the map view a run
// of its own. This holds that debt between the run that created it and the
// filter watcher that pays it.
//
// It is client-owned, lives between two unrelated parts of the tree (a filter
// thunk and a hook mounted with the map), and outlives both, so it is a store
// rather than a ref or Redux state — new filter state does not go in Redux.
// The write path mirrors the map store's view lock: a thunk reaches the store
// through getState(), a component reads it through the hook.
//
// Keyed by entity kind because wells and facilities filter independently and a
// shared slot would let one entity's run cancel the other's debt.
import { create } from "zustand";

import { EntityKind } from "models/entityKind";

export interface MapViewFollowUpStore {
  owed: Record<EntityKind, boolean>;
  setMapViewFollowUpOwed: (entityKind: EntityKind, owed: boolean) => void;
  reset: () => void;
}

const initialState: Pick<MapViewFollowUpStore, "owed"> = {
  owed: { [EntityKind.Well]: false, [EntityKind.Facility]: false }
};

export const useMapViewFollowUpStore = create<MapViewFollowUpStore>()((set, get) => ({
  ...initialState,
  setMapViewFollowUpOwed: (entityKind, owed) => {
    // Writing the same answer again would wake every subscriber for nothing,
    // and the camera reports movement often enough for that to matter.
    if (get().owed[entityKind] === owed) {
      return;
    }
    set({ owed: { ...get().owed, [entityKind]: owed } });
  },
  reset: () => set({ owed: { ...initialState.owed } })
}));

/** Whether the given entity kind's filter still owes the map view a run. */
export const selectMapViewFollowUpOwed =
  (entityKind: EntityKind) =>
  (s: MapViewFollowUpStore): boolean =>
    s.owed[entityKind];
